import { fetchDefaultFulfilmentData } from '~/composables/fetchDefaultFulfilmentData';
import { removeCookie, setCookie } from '~/composables/useCookieManager';
import { useInsightsStore } from '~/stores/insights'; // Import insights store
import type { Client, ClientUser, User } from '~/types/user';

export const useClientsStore = defineStore('clients', {
  state: () => ({
    _selectedId: null as string | null,
    _selectedClient: null as Client | null,
    _locale: 'en',
    _invoiceLocale: null as string | null,
    _currency: 'EUR',
    _dashboardGraphsVisibility: false,
    _dashboardGraphsLoaded: 1,
    _dashboardGraphsLoading: false,
    _modalChangeClientVisibility: false,
  }),

  getters: {
    selectedClientId: state => state._selectedId,
    selectedClient: state => state._selectedClient,
    // /me deliberately still returns disabled clients so the portal can tell
    // them apart; the API refuses every client-scoped call for them, so they are
    // filtered out here rather than offered and then failing.
    clients: (): Client[] => {
      const auth = useAuth();
      return ((auth.user as unknown as User)?.clients || []).filter(
        client => client.status === 'active'
      );
    },
    locale: state => state._locale,
    invoiceLocale: state => state._invoiceLocale,
    currency: state => state._currency,
    visibilityModalChangeClient: state => state._modalChangeClientVisibility,
    dashboardGraphsVisibility: state => state._dashboardGraphsVisibility,
    dashboardGraphsLoading: state => state._dashboardGraphsLoading,
    clientPermissions: (state): string[] => {
      if (!state._selectedId) return [];
      const auth = useAuth();
      const clientUsers = (auth.user as unknown as User)?.client_users || [];
      const clientUser = clientUsers.find(
        (cu: ClientUser) => cu.client_id === state._selectedId
      );
      return clientUser?.permissions || [];
    },
    hasMultipleClients(): boolean {
      return this.clients.length > 1;
    },
  },

  actions: {
    setInvoiceLocale(data: string | null) {
      this._invoiceLocale = data;
    },

    updateVisibility(visibility: boolean) {
      this._modalChangeClientVisibility = visibility;
    },

    updateTotalGraphsLoaded() {
      this._dashboardGraphsLoaded += 1;

      if (this._dashboardGraphsLoaded === 4) {
        this._dashboardGraphsLoading = false;
        this._dashboardGraphsLoaded = 1;
      }
    },

    resetDashboardGraphs() {
      this._dashboardGraphsVisibility = false;
      this._dashboardGraphsLoaded = 1;
      this._dashboardGraphsLoading = false;
    },

    setDashboardGraphsVisibility(visibility: boolean) {
      this._dashboardGraphsVisibility = visibility;
    },

    // Fetch data for the selected client from auth.user
    async getSelectedClientData(clientId: string) {
      if (!clientId) {
        return;
      }

      try {
        // this.clients excludes disabled ones, so a stale selected_client_id
        // cookie pointing at a client that got disabled resolves to nothing
        // instead of loading a dead session.
        const selectedClient = this.clients.find(
          (client: Client) => String(client.id) === String(clientId)
        );

        if (selectedClient) {
          this._selectedClient = selectedClient;
          await updateDefaultFulfilmentData(clientId);
        }
      } catch {
        // Error handling kept but without logging
      }
    },

    // Update the selected client
    async updateSelectedClient(clientId: string) {
      const normalizedId = String(clientId);
      setCookie('selected_client_id', normalizedId);
      this._selectedId = normalizedId;
      await this.getSelectedClientData(normalizedId);
    },

    // Set selected client explicitly
    async setSelectedClient(clientId: string) {
      const normalizedId = String(clientId);
      setCookie('selected_client_id', normalizedId);
      this._selectedId = normalizedId;
      await this.getSelectedClientData(normalizedId);
    },

    // Reset the entire store to its initial state
    resetStore() {
      // Reset all state properties to their default values
      this._selectedId = null;
      this._selectedClient = null;
      this._locale = 'us';
      this._invoiceLocale = null;
      this._currency = 'USD';

      // Reset dashboard graphs state
      this._dashboardGraphsVisibility = false;
      this._dashboardGraphsLoaded = 1;
      this._dashboardGraphsLoading = false;

      // Reset client selector modal
      this._modalChangeClientVisibility = false;

      // Clear the client selection cookie
      removeCookie('selected_client_id');
    },
  },
});

async function updateDefaultFulfilmentData(clientId: string) {
  const insightsStore = useInsightsStore();

  insightsStore.getSelectedOMSShop();
  insightsStore.getSelectedShopGroup();
  insightsStore.getSelectedShopOwner();
  insightsStore.getSelectedDateRange();

  const shops = insightsStore.selectedOMSShop;
  const shopGroups = insightsStore.selectedShopGroup;
  const shopOwners = insightsStore.selectedShopOwner;
  const dateRange = insightsStore.selectedDateRange;

  if (shops.length === 0) {
    await fetchDefaultFulfilmentData(clientId, 'shops');
  }
  if (shopGroups.length === 0) {
    await fetchDefaultFulfilmentData(clientId, 'shopGroups');
  }
  if (shopOwners.length === 0) {
    await fetchDefaultFulfilmentData(clientId, 'shopOwners');
  }
  if (dateRange.length === 0) {
    await fetchDefaultFulfilmentData(clientId, 'dateRange');
  }
}
